import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/userStore";

const AppHome = () => import("../views/Dashboard.vue");
const AppLogin = () => import("../views/Login.vue");
const AppRegister = () => import("../views/Register.vue");
const AppE404 = () => import("../views/E404.vue");
const AppCompanies = () => import("../views/Home.vue");
const AppCompany = () => import("../views/Company.vue");
const AppProject = () => import("../views/ProjectOne.vue");
const AppInvite = () => import("../views/Invite.vue");
const AppVerify = () => import("../views/Verify.vue");
const AppForgot = () => import("../views/Forgot.vue");
const AppSettings = () => import("../views/Settings.vue");
const AppMessages = () => import("../views/Messages.vue");
const AppMessager = () => import("../views/Messager.vue");
const AppMessenger = () => import("../views/Messenger.vue");
const AppCalendar = () => import("../views/Calendar.vue");
const AppMyTasks  = () => import("../views/MyTasks.vue");
const AppTeam = () => import("../views/Team.vue");
const AppAdmin = () => import("../views/Admin.vue");
const AppPaymentSuccess = () => import("../views/PaymentSuccess.vue");
const AppPaymentFail = () => import("../views/PaymentFail.vue");
import { getSiteName } from "@/utils/tools";

const routes = [
    {
        name: "dashboard",
        path: "/",
        component: AppHome,
        meta: { auth: true, title: "HOSTNAME - Home" },
    },
    {
        name: "companies",
        path: "/companies",
        component: AppCompanies,
        meta: { auth: true, title: "HOSTNAME - Projects" },
    },
    {
        name: "company",
        path: "/company/:id",
        component: AppCompany,
        meta: { auth: true, title: "HOSTNAME - Company" },
    },
    {
        name: "project",
        path: "/project/:id",
        component: AppProject,
        meta: { auth: true, title: "HOSTNAME - Project" },
    },
    {
        name: "messages",
        path: "/messages",
        component: AppMessages,
        meta: { auth: true, title: "HOSTNAME - Messages" },
    },
    {
        name: "messager",
        path: "/messager",
        component: AppMessager,
        meta: { auth: true, title: "HOSTNAME - Chat" },
    },
    {
        name: "messenger",
        path: "/messenger",
        component: AppMessenger,
        meta: { auth: true, title: "HOSTNAME - Chat" },
    },
    {
        name: "calendar",
        path: "/calendar",
        component: AppCalendar,
        meta: { 
            auth: true, 
            title: "HOSTNAME - Calendar",
            permissions: {
                features: ["calendar"],
            }
        }
    },
    {
        name: "my-tasks",
        path: "/my-tasks",
        component: AppMyTasks,
        meta: { auth: true, title: "HOSTNAME - MyTasks" },
    },    
    {
        name: "team",
        path: "/team",
        component: AppTeam,
        meta: { auth: true, title: "HOSTNAME - Team" },
    },
    {
        name: "settings",
        path: "/settings",
        component: AppSettings,
        meta: { auth: true, title: "HOSTNAME - Settings" },
    },
    {
        name: "admin",
        path: "/admin",
        component: AppAdmin,
        meta: { auth: true, title: "HOSTNAME - admin panel" },
    },
    {
        name: "login",
        path: "/login",
        component: AppLogin,
        meta: { title: "HOSTNAME - Login" },
    },
    {
        name: "register",
        path: "/register",
        component: AppRegister,
        meta: { title: "HOSTNAME - Register" },
    },
    {
        name: "invite",
        path: "/invite",
        component: AppInvite,
        meta: { title: "HOSTNAME - Invite" },
        props: (route) => ({ code: route.query.code, from: route.query.from }),
    },
    {
        name: "verify",
        path: "/verify",
        component: AppVerify,
        meta: { title: "HOSTNAME - Verity" },
        props: (route) => ({ code: route.query.key }),
    },
    {
        name: "forgot",
        path: "/forgot",
        component: AppForgot,
        meta: { title: "HOSTNAME - Forgot password" },
    },
    {
        name: "payment-success",
        path: "/payment/success",
        component: AppPaymentSuccess,
        meta: { title: "HOSTNAME - Payment Success" },
    },
    {
        name: "payment-fail",
        path: "/payment/fail",
        component: AppPaymentFail,
        meta: { title: "HOSTNAME - Payment Failed" },
    },
    {
        path: "/:any(.*)", // .*
        component: AppE404,
    },
];

const router = createRouter({
    routes,
    history: createWebHistory(),
});

// Функция проверки доступа к маршруту (принимает route как параметр)
function checkRouteAccess(routeMeta, userStore, route) {
  // Если маршрут не требует авторизации - доступ разрешен
  if (!routeMeta.auth) return { allowed: true };
  
  if (!userStore.isAuthenticated) {
    return { 
      allowed: false, 
      redirect: { name: 'login', query: { redirect: route.fullPath } },
      reason: 'Not authenticated'
    };
  }
  
  // Если нет требований по правам - доступ разрешен,
  // Задел на будущее, проверять маршрут еще до перехода по нему,
  // Сейчас такой надобности нет
  
  if (!routeMeta.permissions) {
    return { allowed: true };
  }
 
  return { allowed: true };
}

router.beforeEach(async (to, from, next) => {
  const siteName = getSiteName();
  const userStore = useUserStore();
  
  console.log(`Navigation: ${from.path} -> ${to.path}`);
  
  // Обновляем заголовок
  document.title = to.meta.title?.replace("HOSTNAME", siteName.charAt(0).toUpperCase() + siteName.slice(1)) || 'NexPlan';
  
  // Автоматический логин если есть сохраненная сессия
  if (!userStore.isAuthenticated) {
    await userStore.autoLogin();
  }
  
  // Проверяем доступ к маршруту
  const accessCheck = checkRouteAccess(to.meta, userStore, to);
  
  if (!accessCheck.allowed) {
    console.warn('Route access denied:', {
      route: to.name,
      reason: accessCheck.reason,
      user: {
        roles: userStore.roleTitles,
        license: userStore.licenseTag,
        level: userStore.maxAccessLevel
      }
    });
    
    // Перенаправляем в зависимости от причины отказа
    if (accessCheck.redirect) {
      next(accessCheck.redirect);
    } else if (!userStore.isAuthenticated) {
      // Не авторизован - на логин
      next({ 
        name: 'login', 
        query: { 
          redirect: to.fullPath,
          message: 'Требуется авторизация'
        } 
      });
    } else {
      // Нет прав - на главную
      next({ 
        name: 'dashboard',
        query: { 
          error: 'access_denied',
          message: 'У вас недостаточно прав для доступа к этой странице'
        }
      });
    }
    return;
  }
  
  // Если уже авторизован и пытается зайти на страницы авторизации - редирект
  if ((to.name === 'login' || to.name === 'register') && userStore.isAuthenticated) {
    next({ name: 'dashboard' });
    return;
  }
   
  console.log('Access granted to:', to.name);
  next();
});

export default router;
